import { useEffect, useRef, useState } from "react";

/**
 * <picture> with AVIF sources and a universal 512 WebP <img>, driven by a `resolveImage()` result.
 * On load error it steps through `resolved.fallbacks` (today's URL, then the raw legacy string)
 * instead of re-pinning straight to S3.
 *
 * Only rendered when `resolved.isV2` is true; components keep their existing markup otherwise, so
 * turning the flag off restores today's DOM exactly.
 */
const Picture = ({
  resolved,
  alt = "",
  className,
  imgClassName,
  style,
  width,
  height,
  loading = "lazy",
  fetchpriority,
  decoding = "async",
  onLoad,
  onClick,
  draggable,
}) => {
  const [step, setStep] = useState(-1); // -1 = primary, 0.. = fallbacks
  const srcRef = useRef(resolved?.src);
  const imgRef = useRef(null);

  // Reset the fallback chain *during* render, not in an effect: carousel slides (key={index}) and
  // list items reuse this instance for a different image, and a post-render reset would leave the
  // first render on the previous image's fallback — or on `undefined` when the new chain is
  // shorter. React re-renders with the new state before committing this one.
  let current = step;
  if (srcRef.current !== resolved?.src) {
    srcRef.current = resolved?.src;
    current = -1;
    if (step !== -1) setStep(-1);
  }
  const stepRef = useRef(current);
  stepRef.current = current;

  const hasImage = !!(resolved && resolved.src);
  const usingFallback = current >= 0;
  const src = !hasImage ? undefined : usingFallback ? resolved.fallbacks?.[current] : resolved.src;

  // An error that fired before React attached onError (e.g. carousel clones, hydration) is
  // otherwise lost: a complete image with no pixels means the primary candidate failed. This can
  // only happen for the candidate the element mounts with — once React has attached onError, that
  // handler owns every later failure — so it runs once per image, on the primary only. Without
  // that bound (and without a dependency array) it re-fired on every ancestor render and burned
  // the whole chain on any zero-pixel image that had not actually errored.
  useEffect(() => {
    if (!src || stepRef.current !== -1) return;
    const el = imgRef.current;
    if (
      el &&
      el.complete &&
      el.naturalWidth === 0 &&
      (resolved?.fallbacks || []).length > stepRef.current + 1
    ) {
      setStep(stepRef.current + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!hasImage) return null;

  const onError = () => {
    const next = stepRef.current + 1;
    if (next < (resolved.fallbacks || []).length) setStep(next);
  };

  const img = (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={imgClassName || className}
      style={style}
      width={width || resolved.width}
      height={height || resolved.height}
      sizes={!usingFallback ? resolved.sizes : undefined}
      loading={loading}
      decoding={decoding}
      // React 18 warns on camelCase for this attribute; lowercase reaches the DOM as-is.
      fetchpriority={fetchpriority}
      onLoad={onLoad}
      onError={onError}
      onClick={onClick}
      draggable={draggable}
    />
  );

  // Once we are on a fallback, drop the AVIF sources too: the browser would otherwise keep
  // choosing a <source> and never use the fallback <img src>.
  if (usingFallback || !resolved.sources?.length) return img;

  return (
    <picture className={imgClassName ? className : undefined}>
      {resolved.sources.map((s) => (
        <source key={s.type} type={s.type} srcSet={s.srcSet} sizes={s.sizes} />
      ))}
      {img}
    </picture>
  );
};

export default Picture;
